import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

const TOOBIT_REST_BASE = 'https://api.toobit.com';
const TOOBIT_WS_URL = 'wss://stream.toobit.com/quote/ws/v1';
const TOOBIT_BOOK_DEPTH = 1000;
const TOOBIT_MAX_CANDLES = 1000;
const TOOBIT_MAX_TRADES = 100;
const TOOBIT_HEARTBEAT_INTERVAL_MS = 30_000;

export const toobitRestClient = proxyRestOptions.client!;

type ToobitNumber = number | string;
type ToobitBookLevel = [price: ToobitNumber, size: ToobitNumber];

interface ToobitFilter {
  filterType: string;
  tickSize?: ToobitNumber;
}

interface ToobitSymbol {
  symbol: string;
  status: string;
  filters: ToobitFilter[];
}

interface ToobitExchangeInfo {
  symbols: ToobitSymbol[];
}

interface ToobitTicker {
  t: number;
  s: string;
  c: ToobitNumber;
  o: ToobitNumber;
  h: ToobitNumber;
  l: ToobitNumber;
  v: ToobitNumber;
  qv: ToobitNumber;
}

export interface ToobitOrderbookSnapshot {
  t?: number;
  b: ToobitBookLevel[];
  a: ToobitBookLevel[];
}

export type ToobitCandle = [
  timestamp: ToobitNumber,
  open: ToobitNumber,
  high: ToobitNumber,
  low: ToobitNumber,
  close: ToobitNumber,
  volume: ToobitNumber,
  ...metadata: ToobitNumber[],
];

interface ToobitTrade {
  v: ToobitNumber;
  t: number;
  p: ToobitNumber;
  q: ToobitNumber;
  m?: boolean;
  ibm?: boolean;
}

interface ToobitWsData {
  s?: string;
  t?: number;
  st?: number;
  v?: ToobitNumber;
  b?: ToobitBookLevel[];
  a?: ToobitBookLevel[];
  c?: ToobitNumber;
  o?: ToobitNumber;
  o24h?: ToobitNumber;
  h?: ToobitNumber;
  h24h?: ToobitNumber;
  l?: ToobitNumber;
  l24h?: ToobitNumber;
  v24h?: ToobitNumber;
  qv?: ToobitNumber;
  qv24h?: ToobitNumber;
  m24h?: ToobitNumber;
  p?: ToobitNumber;
  q?: ToobitNumber;
  m?: boolean | ToobitNumber;
}

interface ToobitWsMessage {
  symbol?: string;
  topic?: string;
  data?: ToobitWsData[];
  params?: { klineType?: string };
  ping?: number;
  pong?: number;
  code?: number | string;
  msg?: string;
}

export interface ToobitOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
}

const TOOBIT_TIMEFRAMES: Partial<Record<IChartTimeframe, string>> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '4h': '4h',
  '24h': '1d',
  '1d': '1d',
  '168h': '1w',
  '1w': '1w',
  '1M': '1M',
};

const TOOBIT_INTERVAL_BY_MILLISECONDS: Record<number, string> = {
  60_000: '1m',
  300_000: '5m',
  900_000: '15m',
  1_800_000: '30m',
  3_600_000: '1h',
  14_400_000: '4h',
  86_400_000: '1d',
  604_800_000: '1w',
};

const isFiniteNumber = (value: unknown) => Number.isFinite(Number(value));
const asString = (value: ToobitNumber) => String(value);

const normalizeBookLevels = (levels: ToobitBookLevel[] | undefined): string[][] =>
  (levels ?? [])
    .filter(
      level =>
        Array.isArray(level) &&
        level.length >= 2 &&
        isFiniteNumber(level[0]) &&
        isFiniteNumber(level[1]),
    )
    .map(([price, size]) => [asString(price), asString(size)]);

const readTicker = (payload: ToobitTicker | ToobitTicker[], symbol: string): ToobitTicker => {
  const items = Array.isArray(payload) ? payload : [payload];
  const ticker = items.find(item => item?.s === symbol);
  if (!ticker) throw new Error(`Toobit ticker ${symbol} is unavailable`);
  return ticker;
};

const calculateChange = (close: ToobitNumber, open: ToobitNumber): string => {
  const closeNumber = Number(close);
  const openNumber = Number(open);
  if (!Number.isFinite(closeNumber) || !Number.isFinite(openNumber) || openNumber === 0) {
    return '0';
  }
  return normalizeNumber(((closeNumber - openNumber) / openNumber) * 100);
};

export const buildToobitMarketSymbol = (base: string, quote: string) =>
  `${base.toUpperCase()}${quote.toUpperCase()}`;

export const normalizeToobitCandles = (candles: ToobitCandle[]): KLine[] =>
  candles
    .filter(
      candle =>
        Array.isArray(candle) && candle.length >= 6 && candle.slice(0, 6).every(isFiniteNumber),
    )
    .map(
      ([timestamp, open, high, low, close, volume]) =>
        [
          Number(timestamp),
          asString(open),
          asString(high),
          asString(low),
          asString(close),
          asString(volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const toobitPrecisionFromTick = (tick: ToobitNumber | undefined): number => {
  if (tick === undefined || !Number.isFinite(Number(tick)) || Number(tick) <= 0) return 1;

  const [, decimals = ''] = String(tick).split('.');
  return decimals.replace(/0+$/, '').length;
};

export const buildToobitSubscriptions = (symbol: string, interval: string) =>
  ['depth', 'trade', 'realtimes', `kline_${interval}`].map(topic => ({
    symbol,
    topic,
    event: 'sub' as const,
    params: { binary: false },
  }));

export const createToobitOrderbookState = (): ToobitOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
});

export const applyToobitOrderbookSnapshot = (
  state: ToobitOrderbookState,
  snapshot: Pick<ToobitOrderbookSnapshot, 'b' | 'a'>,
) => {
  const bids = normalizeBookLevels(snapshot.b);
  const asks = normalizeBookLevels(snapshot.a);
  if (bids.length === 0 || asks.length === 0) {
    throw new Error('Toobit orderbook snapshot is empty');
  }

  state.bids.clear();
  state.asks.clear();
  bids.forEach(([price, size]) => state.bids.set(price, size));
  asks.forEach(([price, size]) => state.asks.set(price, size));
};

export class ToobitStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  orderbook = createToobitOrderbookState();
  heartbeatTimer: ReturnType<typeof setInterval> | undefined;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildToobitMarketSymbol(base, quote);
    this.displayDepth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const asks = [...this.orderbook.asks.entries()]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.displayDepth);
    const bids = [...this.orderbook.bids.entries()]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.displayDepth);
    this.onOrderbookUpdate({ asks, bids });
  };

  publishTicker = ({
    close,
    open,
    high,
    low,
    baseVolume,
    quoteVolume,
    change,
  }: {
    close: ToobitNumber;
    open: ToobitNumber;
    high: ToobitNumber;
    low: ToobitNumber;
    baseVolume: ToobitNumber;
    quoteVolume: ToobitNumber;
    change?: string;
  }) => {
    if (
      !isFiniteNumber(close) ||
      !isFiniteNumber(open) ||
      !isFiniteNumber(high) ||
      !isFiniteNumber(low) ||
      !isFiniteNumber(baseVolume) ||
      !isFiniteNumber(quoteVolume)
    ) {
      throw new Error(`Toobit ticker ${this.pair} has an invalid shape`);
    }
    const last = asString(close);
    this.onTickerUpdate({
      baseVolume: asString(baseVolume),
      change: change ?? calculateChange(close, open),
      close: last,
      high: asString(high),
      last,
      low: asString(low),
      quoteVolume: asString(quoteVolume),
    });
  };

  fetchMarket = async () => {
    try {
      const response = await toobitRestClient.get<ToobitExchangeInfo>(
        `${TOOBIT_REST_BASE}/api/v1/exchangeInfo`,
      );
      const symbol = response.data.symbols?.find(item => item.symbol === this.pair);
      if (!symbol || symbol.status.toUpperCase() !== 'TRADING') {
        throw new Error(`Toobit market ${this.pair} is unavailable`);
      }
      const tick = symbol.filters?.find(filter => filter.filterType === 'PRICE_FILTER')?.tickSize;
      if (!isFiniteNumber(tick) || Number(tick) <= 0) {
        throw new Error(`Toobit price tick ${this.pair} is unavailable`);
      }
      this.priceTick = asString(tick!);
      this.precision = toobitPrecisionFromTick(tick);
      this.selectedPrecision = this.precision;
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Toobit market error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await toobitRestClient.get<ToobitOrderbookSnapshot>(
        `${TOOBIT_REST_BASE}/quote/v1/depth`,
        {
          params: {
            limit: TOOBIT_BOOK_DEPTH,
            symbol: this.pair,
          },
        },
      );
      applyToobitOrderbookSnapshot(this.orderbook, response.data);
      this.publishOrderbook();
    } catch (error) {
      console.log('Toobit orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await toobitRestClient.get<ToobitTicker | ToobitTicker[]>(
        `${TOOBIT_REST_BASE}/quote/v1/ticker/24hr`,
        { params: { symbol: this.pair } },
      );
      const ticker = readTicker(response.data, this.pair);
      this.publishTicker({
        close: ticker.c,
        open: ticker.o,
        high: ticker.h,
        low: ticker.l,
        baseVolume: ticker.v,
        quoteVolume: ticker.qv,
      });
    } catch (error) {
      console.log('Toobit ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const toobitInterval = TOOBIT_TIMEFRAMES[interval];
    if (!toobitInterval) return [];
    const boundedLimit = Math.min(TOOBIT_MAX_CANDLES, Math.max(1, limit));

    try {
      const response = await toobitRestClient.get<ToobitCandle[]>(
        `${TOOBIT_REST_BASE}/quote/v1/klines`,
        {
          params: {
            endTime: to > 0 ? to - 1 : Date.now(),
            interval: toobitInterval,
            limit: boundedLimit,
            startTime: from > 0 ? from : undefined,
            symbol: this.pair,
          },
        },
      );
      return normalizeToobitCandles(response.data ?? [])
        .filter(row => (from <= 0 || row[0] >= from) && (to <= 0 || row[0] < to))
        .slice(-boundedLimit);
    } catch (error) {
      console.log('Toobit klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    const boundedLimit = Math.min(TOOBIT_MAX_TRADES, Math.max(1, limit));
    try {
      const response = await toobitRestClient.get<ToobitTrade[]>(
        `${TOOBIT_REST_BASE}/quote/v1/trades`,
        { params: { limit: boundedLimit, symbol: this.pair } },
      );
      if (!Array.isArray(response.data)) {
        throw new Error(`Toobit trades ${this.pair} have an invalid shape`);
      }
      return response.data
        .filter(
          trade => isFiniteNumber(trade.t) && isFiniteNumber(trade.p) && isFiniteNumber(trade.q),
        )
        .map(trade => ({
          price: asString(trade.p),
          quantity: asString(trade.q),
          timestamp: String(trade.t),
          side: (trade.ibm ?? (typeof trade.m === 'boolean' && trade.m)) ? 'sell' : 'buy',
        }));
    } catch (error) {
      console.log('Toobit trades error:', error);
      return [];
    }
  };

  handleWebSocketMessage = (ws: ReconnectingWebSocket, message: ToobitWsMessage) => {
    if (message.ping !== undefined) {
      ws.send(JSON.stringify({ pong: message.ping }));
      return;
    }
    if (message.code !== undefined && Number(message.code) !== 0) {
      console.log('Toobit websocket subscription error:', message.code, message.msg);
      return;
    }
    if (!Array.isArray(message.data)) return;

    if (message.topic === 'depth') {
      const snapshot = message.data.find(
        item => (item.s ?? message.symbol)?.toUpperCase() === this.pair,
      );
      if (!snapshot?.b || !snapshot.a) return;
      applyToobitOrderbookSnapshot(this.orderbook, { b: snapshot.b, a: snapshot.a });
      this.publishOrderbook();
      return;
    }

    if (message.topic === 'realtimes') {
      const ticker = message.data.find(
        item => (item.s ?? message.symbol)?.toUpperCase() === this.pair,
      );
      if (!ticker?.c) return;
      const open = ticker.o24h ?? ticker.o;
      const high = ticker.h24h ?? ticker.h;
      const low = ticker.l24h ?? ticker.l;
      const baseVolume = ticker.v24h ?? ticker.v;
      const quoteVolume = ticker.qv24h ?? ticker.qv;
      if (
        open === undefined ||
        high === undefined ||
        low === undefined ||
        baseVolume === undefined ||
        quoteVolume === undefined
      ) {
        return;
      }
      const changeRatio = Number(ticker.m24h);
      this.publishTicker({
        close: ticker.c,
        open,
        high,
        low,
        baseVolume,
        quoteVolume,
        change: Number.isFinite(changeRatio)
          ? normalizeNumber(changeRatio * 100)
          : calculateChange(ticker.c, open),
      });
      return;
    }

    if (message.topic === 'trade') {
      if (message.symbol?.toUpperCase() !== this.pair) return;
      [...message.data]
        .sort((left, right) => Number(left.t) - Number(right.t))
        .forEach(trade => {
          if (
            !isFiniteNumber(trade.t) ||
            !isFiniteNumber(trade.p) ||
            !isFiniteNumber(trade.q) ||
            typeof trade.m !== 'boolean'
          ) {
            return;
          }
          this.onTradeUpdate({
            price: asString(trade.p!),
            quantity: asString(trade.q!),
            timestamp: String(trade.t),
            side: trade.m ? 'sell' : 'buy',
          });
        });
      return;
    }

    if (message.topic === 'kline') {
      if (message.params?.klineType !== undefined && !message.params.klineType) return;
      [...message.data]
        .filter(item => (item.s ?? message.symbol)?.toUpperCase() === this.pair)
        .sort((left, right) => Number(left.st ?? left.t) - Number(right.st ?? right.t))
        .forEach(candle => {
          const timestamp = Number(candle.st ?? candle.t);
          if (
            !Number.isFinite(timestamp) ||
            !isFiniteNumber(candle.o) ||
            !isFiniteNumber(candle.h) ||
            !isFiniteNumber(candle.l) ||
            !isFiniteNumber(candle.c) ||
            !isFiniteNumber(candle.v)
          ) {
            return;
          }
          this.klineUpdate([
            timestamp,
            asString(candle.o!),
            asString(candle.h!),
            asString(candle.l!),
            asString(candle.c!),
            asString(candle.v!),
          ]);
        });
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    const candleInterval = TOOBIT_INTERVAL_BY_MILLISECONDS[interval] ?? '1m';
    const ws = new ReconnectingWebSocket(TOOBIT_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    ws.onopen = () => {
      this.orderbook.bids.clear();
      this.orderbook.asks.clear();
      if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
      buildToobitSubscriptions(this.pair, candleInterval).forEach(subscription => {
        ws.send(JSON.stringify(subscription));
      });
      this.heartbeatTimer = setInterval(() => {
        ws.send(JSON.stringify({ ping: Date.now() }));
      }, TOOBIT_HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        this.handleWebSocketMessage(ws, JSON.parse(event.data) as ToobitWsMessage);
      } catch (error) {
        console.log('Toobit websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchMarket(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new ToobitStrategy(args);
    await bootstrapStrategy(strategy);
    return strategy;
  };
}
